import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { DataRulesDataService } from '../data-rules-data.service';
import { AllPrimaryEntitiesNames } from '../data-rules-model';
import { IFieldMeta, MetaDataService, ToManyMeta } from '../meta-data.service';
import { IOptionItem } from './autocomplete/autocomplete.component';
import { IChipItem } from './chip-list-autocomplete/chip-list-autocomplete.component';

export interface IRuleOptionsItem {
  value: string;
  label: string;
  entity: AllPrimaryEntitiesNames;
  used?: boolean;
}

// TODO: remove duplication and rename interfaces / classes

export interface IProvider<T> {
  optionsLength: number;

  filter(value: any): Observable<T[]>;

  get(name: number | string): T;
}

export class StaticOptionsProvider implements IProvider<IOptionItem> {

  private readonly _options: IOptionItem[];

  constructor(options: IOptionItem[], sort: boolean = true) {
    this._options = sort ? this.sortOptions(options) : options;
  }

  filter(value: any): Observable<IOptionItem[]> {
    if (typeof value === 'number')    // BW: figure out when this happens and add comment
      return of(this._options);
    const results = value.length > 0 ? this._options.filter(opt => this.filterOptionResults(value, opt)) : this._options;
    return of(results);
  }

  get optionsLength(): number {
    return this._options.length;
  }

  get(name: number | string): IOptionItem {
    return this._options.find(opt => opt.name === name);
  }

  private sortOptions(options: IOptionItem[]): IOptionItem[] {
    return options.sort((a, b) => {
      const aLowerCase = a.label.toLowerCase();
      const bLowerCase = b.label.toLowerCase();
      if (aLowerCase < bLowerCase) return -1;
      if (aLowerCase > bLowerCase) return 1;
      return 0;
    });
  }

  filterOptionResults(value: any, option: IOptionItem): boolean {

    value = value.toLowerCase().replace(/\s/g, '');
    const name = option.name.toString();
    const label = option.label;

    return name.toLowerCase().replace(/\s/g, '').includes(value)
      || label.toLowerCase().replace(/\s/g, '').includes(value);

  }
}

export class RuleOptionsProvider implements IProvider<IRuleOptionsItem> {

  private readonly _options: IRuleOptionsItem[];

  constructor(options: IRuleOptionsItem[], sort: boolean = true) {
    this._options = sort ? this.sortOptions(options) : options;
  }

  get optionsLength(): number {
    return this._options.length;
  }

  filter(value: any): Observable<IRuleOptionsItem[]> {
    const results = value.length > 0 ? this._options.filter(opt => this.filterOptionResults(value, opt)) : this._options;
    return of(results);
  }

  get(value: number | string): IRuleOptionsItem {
    return this._options.find(opt => opt.value === value);
  }

  private sortOptions(options: IRuleOptionsItem[]): IRuleOptionsItem[] {
    return options.sort((a, b) => {
      return DataRulesDataService.ruleNameSort(a.label, b.label);
    });
  }

  private filterOptionResults(value: any, option: IRuleOptionsItem): boolean {

    value = value.toLowerCase().replace(/\s/g, '');
    const optionValue = option.value;
    const optionsLabel = option.label;

    return optionValue.toLowerCase().replace(/\s/g, '').includes(value)
      || optionsLabel.toLowerCase().replace(/\s/g, '').includes(value);

  }
}

export class CountryOptionsProvider implements IProvider<IOptionItem> {

  private readonly _metaDataService: MetaDataService;
  private _options: IOptionItem[];

  constructor(metaDataService: MetaDataService, selOptions: IOptionItem[] = []) {
    this._metaDataService = metaDataService;
    this._options = selOptions;
  }

  filter(value: string): Observable<IOptionItem[]> {
    // value can be an empty string if the user deletes all the text
    // value will be a number (the record id of the item selected) when the user selects an item from the dropdown
    if (!value || typeof value !== 'string' || value.length === 0)
      return of([]);

    const entityName = 'Country';

    return this._metaDataService.lookUp(entityName, value).pipe(
      map(metas => metas.sort((a, b) => {
        if (a.name < b.name)
          return -1;
        if (a.name > b.name)
          return 1;
        return 0;
      })),
      map(metas => metas.map(m => ({name: m.id, label: m.name}))),
      tap(options => this._options = options),
    );
  }

  get optionsLength(): number {
    return this._options.length;
  }

  get(name: number | string): IOptionItem {
    return this._options.find(opt => opt.name === name);
  }
}

export class DynamicOptionsProvider implements IProvider<IOptionItem> {

  private readonly _metaDataService: MetaDataService;
  private readonly _fieldMeta: IFieldMeta;
  private _options: IOptionItem[];

  constructor(metaDataService: MetaDataService, fieldMeta: IFieldMeta, selOptions: IOptionItem[] = []) {
    this._metaDataService = metaDataService;
    this._fieldMeta = fieldMeta;
    this._options = selOptions;
  }

  filter(value: string): Observable<IOptionItem[]> {
    // value can be an empty string if the user deletes all the text
    // value will be a number (the record id of the item selected) when the user selects an item from the dropdown
    if (!value || typeof value !== 'string' || value.length === 0)
      return of([]);

    let entityName;
    if (this._fieldMeta.metaType === 'TO_ONE')
      entityName = this._fieldMeta.toOneType;
    else if (this._fieldMeta.metaType === 'TO_MANY')
      entityName = this._fieldMeta.toManyType;
    else if (this._fieldMeta.metaType === 'COUNTRY')
      entityName = 'Country';

    return this._metaDataService.lookUp(entityName, value).pipe(
      map(metas => metas.map(m => ({name: m.id, label: m.name}))),
      tap(options => this._options = options),
    );
  }

  get optionsLength(): number {
    return this._options.length;
  }

  get(name: number | string): IOptionItem {
    return this._options.find(opt => opt.name === name);
  }
}

export abstract class AbstractItemsProvider implements IProvider<IChipItem> {
  protected constructor(fieldMeta: IFieldMeta, items: IChipItem[] = [],
                        additionalFilters: ((field: IFieldMeta, data: ToManyMeta[]) => ToManyMeta[])[] = []) {
    this._fieldMeta = fieldMeta;
    this._items = items;
    this._additionalFilters = additionalFilters;
  }

  protected readonly _fieldMeta: IFieldMeta;
  protected _items: IChipItem[];
  protected readonly _additionalFilters: ((field: IFieldMeta, data: ToManyMeta[]) => ToManyMeta[])[] = [];

  public abstract filter(value: string): Observable<IChipItem[]>;

  get optionsLength(): number {
    return this._items.length;
  }

  get(id: number): IChipItem {
    return this._items.find(opt => opt.id === id);
  }
}

export class DynamicItemsProvider extends AbstractItemsProvider {

  private readonly _metaDataService: MetaDataService;

  constructor(metaDataService: MetaDataService, fieldMeta: IFieldMeta, selItems: IChipItem[] = [],
              additionalFilters: ((field: IFieldMeta, data: ToManyMeta[]) => ToManyMeta[])[] = []) {
    super(fieldMeta, selItems, additionalFilters);
    this._metaDataService = metaDataService;
  }

  filter(value: string): Observable<IChipItem[]> {
    // value can be an empty string if the user deletes all the text
    // value will be a number (the record id of the item selected) when the user selects an item from the dropdown
    if (!value || typeof value !== 'string' || value.length === 0)
      return of([]);

    const entityName = this._fieldMeta.metaType === 'TO_ONE' ? this._fieldMeta.toOneType : this._fieldMeta.toManyType;
    return this._metaDataService.lookUp(entityName, value).pipe(
      map(metas => {
        this._additionalFilters.forEach(af => metas = af(this._fieldMeta, metas));
        return metas;
      }),
      map(metas => metas.map(m => ({id: m.id, name: m.name}))),
      tap(items => this._items = items),
    );
  }
}

export class DynamicCertificationsProvider implements IProvider<IOptionItem> {
  private _entityName;
  private readonly _metaDataService: MetaDataService;
  private _options: IOptionItem[];

  constructor(metaDatService: MetaDataService, options, entityName: string) {
    this._metaDataService = metaDatService;
    this._options = options;
    this._entityName = entityName;
  }

  filter(value: string): Observable<IOptionItem[]> {
    if (!value) {
      return of([]);
    }

    return this._metaDataService.lookUp(this._entityName, value).pipe(
      map(metas => metas.map(m => ({name: m.id, label: m.name} as IOptionItem))),
      tap(options => this._options = options),
    );
  }

  get optionsLength(): number {
    return this._options.length;
  }

  get(name: number): IOptionItem {
    return this._options.find(opt => opt.name === name);
  }
}
